import { useState } from "react";
import { Star, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=in.brimi.twa";
const PLAY_STORE_INTENT = "market://details?id=in.brimi.twa";

// True inside a Trusted Web Activity (Play Store install). We use this to
// open the native Play Store rating sheet instead of an in-app form.
function isTwa() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches &&
    /Android/i.test(navigator.userAgent)
  );
}

/**
 * Star selector + optional comment. Submits to /api/feedback. When running
 * inside the Play Store wrapper, the "Rate" button links straight to the
 * Play Store rating dialog which then feeds Google's review count.
 */
export function RateAppModal({ open, onClose, user }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const twa = isTwa();

  if (!open) return null;

  const submit = async () => {
    if (rating === 0) {
      toast.error("Tap a star first ⭐");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", { rating, comment: comment.trim() });
      toast.success("Thanks for the feedback! 💛");
      // 5-star users → nudge to Play Store review too
      if (rating >= 4) {
        setTimeout(() => {
          if (
            window.confirm(
              "Would you like to share this on the Play Store too? It really helps other students find Brimi.",
            )
          ) {
            window.location.href = twa ? PLAY_STORE_INTENT : PLAY_STORE_URL;
          }
        }, 400);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openPlayStore = () => {
    window.location.href = twa ? PLAY_STORE_INTENT : PLAY_STORE_URL;
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="rate-modal-overlay"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-2xl border-2 border-border shadow-2xl max-w-sm w-full p-6 relative"
        data-testid="rate-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="rate-modal-close"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-foreground/5 hover:bg-foreground/10 flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center">
          <div className="text-3xl mb-1">💛</div>
          <h2 className="text-xl font-black">How&apos;s Brimi treating you?</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.name ? `Hey ${user.name.split(" ")[0]}, your` : "Your"}{" "}
            feedback helps us build a better tutor for every ICSE student.
          </p>
        </div>

        <div
          className="flex justify-center gap-2 mt-6"
          data-testid="rate-star-row"
          onMouseLeave={() => setHover(0)}
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || rating) >= n;
            return (
              <button
                key={n}
                data-testid={`rate-star-${n}`}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHover(n)}
                className="p-1 transition-transform hover:scale-110"
                type="button"
              >
                <Star
                  className={`w-9 h-9 ${active ? "fill-[hsl(var(--brimi-yellow))] text-[hsl(var(--brimi-yellow))]" : "text-foreground/25"}`}
                />
              </button>
            );
          })}
        </div>
        {rating > 0 && (
          <div className="text-center text-xs font-bold text-muted-foreground mt-2">
            {rating === 5 && "You're amazing! 🎉"}
            {rating === 4 && "Glad you're enjoying it 😊"}
            {rating === 3 && "Thanks — tell us what to improve ⬇️"}
            {rating <= 2 && "Sorry! Tell us what went wrong ⬇️"}
          </div>
        )}

        <textarea
          data-testid="rate-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            rating >= 4
              ? "What do you love the most? (optional)"
              : "What could be better? We read every message."
          }
          rows={3}
          maxLength={800}
          className="mt-5 w-full rounded-xl border border-border bg-muted/50 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brimi-blue))]/40"
        />
        <div className="text-[10px] text-right text-muted-foreground mt-1">
          {comment.length}/800
        </div>

        <button
          data-testid="rate-submit"
          onClick={submit}
          disabled={submitting || rating === 0}
          className="mt-3 w-full pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-sm font-bold py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          Send feedback
        </button>

        {twa && (
          <button
            data-testid="rate-playstore-btn"
            onClick={openPlayStore}
            className="mt-2 w-full pill-btn bg-foreground/5 hover:bg-foreground/10 text-xs font-bold py-2 text-foreground"
          >
            Or rate us directly on the Play Store →
          </button>
        )}
      </div>
    </div>
  );
}
